import {
  AfterViewInit,
  inject,
  Injectable,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { timeout, catchError, map } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { environment } from '../environments/environment';
import {
  AnioVehiculoResponse,
  Auto,
  MarcaVehiculo,
  MarcaVehiculoResponse,
  ModeloAniosAplica,
  ModeloVehiculo,
  ModeloVehiculoResponse,
  MotorVehiculoResponse,
  SubmodeloVehiculoResponse,
  VinDecodeResponse,
} from '../types/vehicle.types';
import { ApiMembresiaResponse } from '../types/API.types';
import {
  LOCAL_STORAGE_KEY_AUTH_TOKEN,
  LOCAL_STORAGE_KEY_GARAGE_VEHICLES,
  LOCAL_STORAGE_KEY_USER_DATA,
} from '../config/variables';
import { isPlatformBrowser } from '@angular/common';
import { User } from '../types/auth.types';

const headers = {
  Authorization: '3A6E5ADB58DD1D45170514CD6A60EA32',
  'Content-Type': 'application/json',
  token: '1234',
};

@Injectable({
  providedIn: 'root',
})
export class VehicleService {
  private apiUrl = `${environment.apiUrl}/${environment.apiVersion}`;

  isLoggedIn = false;
  private platformId = inject(PLATFORM_ID);

  private vehiclesSubject = new BehaviorSubject<Auto[]>([]);
  public vehicles$ = this.vehiclesSubject.asObservable();

  constructor(private http: HttpClient) {
    this.refresh();
  }

  private refresh() {
    this.get();
  }

  private setIsLoggedIn() {
    this.isLoggedIn = this.checkIsLoggedIn();
  }

  private checkIsLoggedIn() {
    const isBrowser = isPlatformBrowser(this.platformId);

    if (!isBrowser) {
      return false;
    }

    const token = this.getToken();
    const userData = this.getUserData();

    return !!(token && userData);
  }

  private getToken() {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    return localStorage.getItem(LOCAL_STORAGE_KEY_AUTH_TOKEN);
  }

  private getUserData() {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    return localStorage.getItem(LOCAL_STORAGE_KEY_USER_DATA)
      ? (JSON.parse(
          localStorage.getItem(LOCAL_STORAGE_KEY_USER_DATA) || '{}'
        ) as User)
      : null;
  }

  async decodeVin(vin: string): Promise<Auto | null> {
    this.setIsLoggedIn();
    try {
      const url = `https://allasenlinea.com:4554/api/vehiculos/getDecodeVin/${vin}/false`;

      const response = await this.http
        .get<VinDecodeResponse[]>(url, { headers })
        .toPromise();

      if (response && response.length > 0) {
        const vehicleData = response[0];

        const idSubmodeloMotor = vehicleData.id_submodelo_motor;

        const vehicle = await firstValueFrom(
          this.getAutoFromIdSubmodeloMotor(idSubmodeloMotor)
        );

        if (!vehicle) {
          return null;
        }

        return { ...vehicle, vin: vin, idSubmodeloMotor: idSubmodeloMotor };
      }

      return null;
    } catch (error) {
      console.error('Error decoding VIN:', error);
      return null;
    }
  }

  getAutoFromIdSubmodeloMotor(idSubmodeloMotor: number) {
    this.setIsLoggedIn();
    const idUsuarioDigital = this.getUserData()?.idUsuarioDigital || 0;
    const url = `${this.apiUrl}/vehicles/auto/${idSubmodeloMotor}/${idUsuarioDigital}`;
    return this.http.get<ApiMembresiaResponse<Auto>>(url, { headers }).pipe(
      map((response) => {
        return response?.data || null;
      }),
      catchError((error) => {
        console.error(
          '❌ Error obteniendo vehículo desde ID de submodelo motor:',
          error
        );
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtiene los años de los vehículos
   * @returns Observable con la respuesta de años
   */
  getAnios() {
    const url = `${environment.apiUrl}/v1/catalogo-vehiculos/anios`;

    return this.http.get<AnioVehiculoResponse[]>(url, { headers }).pipe(
      catchError((error) => {
        console.error('❌ Error obteniendo años:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtiene las marcas de los vehículos
   * @param year
   * @returns
   */
  getMarcas(year: number) {
    const url = `${environment.apiUrl}/v1/catalogo-vehiculos/marcas/${year}`;

    return this.http.get<MarcaVehiculoResponse[]>(url, { headers }).pipe(
      map((response) => {
        return response;
      }),
      catchError((error) => {
        console.error('❌ Error obteniendo marcas de vehículos:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtiene los modelos de los vehículos
   * @param year
   * @param idMarca
   * @returns
   */
  getModelos(year: number, idMarca: number) {
    const url = `${environment.apiUrl}/v1/catalogo-vehiculos/modelos/${year}/${idMarca}`;

    return this.http.get<ModeloVehiculoResponse[]>(url, { headers }).pipe(
      map((response) => {
        return response;
      }),
      catchError((error) => {
        console.error('❌ Error obteniendo modelos de vehículos:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtiene los submodelos de los vehículos
   * @param idModelo
   * @returns
   */
  getSubmodelos(idModelo: number) {
    const url = `${environment.apiUrl}/v1/catalogo-vehiculos/submodelos/${idModelo}`;

    return this.http
      .get<{ data: SubmodeloVehiculoResponse[] }>(url, { headers })
      .pipe(
        map((response) => {
          return response.data;
        }),
        catchError((error) => {
          console.error('❌ Error obteniendo submodelos de vehículos:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Obtiene los motores de los vehículos
   * @param idModelo
   * @param descripcionSubModelo
   * @returns
   */
  getMotores(idModelo: number, descripcionSubModelo: string) {
    // Cambia la URL a tu API de NestJS
    const url = `${environment.apiUrl}/v1/catalogo-vehiculos/motores/${idModelo}/${descripcionSubModelo}`;

    return this.http.get<MotorVehiculoResponse[]>(url, { headers }).pipe(
      map((response) => {
        return response;
      }),
      catchError((error) => {
        console.error('❌ Error obteniendo motores de vehículos:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtiene los vehículos más buscados (top)
   * @returns Observable con la respuesta de vehículos top
   */
  getVehicleBrands() {
    const url = `${this.apiUrl}${environment.endpoints.vehicles.getAllBrands}`;

    return this.http.get<ApiMembresiaResponse<MarcaVehiculo[]>>(url).pipe(
      map((response) => {
        return response;
      }),
      catchError((error) => {
        console.error('❌ Error obteniendo marcas de vehículos:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtiene vehículos por marca
   * @param params Parámetros de consulta (limit, offset)
   * @returns Observable con la respuesta de vehículos por marca
   */
  getVehicleModels(limit?: number) {
    let url = `${this.apiUrl}${environment.endpoints.vehicles.getAllModels}`;
    if (limit) {
      url += `?limit=${limit}`;
    }

    return this.http.get<ApiMembresiaResponse<ModeloVehiculo[]>>(url).pipe(
      map((response) => {
        return response;
      }),
      map((response) => {
        return response;
      }),
      catchError((error) => {
        console.error('❌ Error obteniendo modelos de vehículos:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtiene los años aplicables por modelo de vehículo
   * @returns Observable con la respuesta de modelos con años aplicables
   */
  getVehicleModelsAnios(limit?: number) {
    let url = `${this.apiUrl}${environment.endpoints.vehicles.getModelosAnios}`;
    if (limit) {
      url += `?limit=${limit}`;
    }

    return this.http.get<ApiMembresiaResponse<ModeloAniosAplica[]>>(url).pipe(
      map((response) => {
        return response;
      }),
      catchError((error) => {
        console.error('❌ Error obteniendo años aplicables de modelos:', error);
        return throwError(() => error);
      })
    );
  }

  // ############### METODOS PARA EL GARAGE ###############
  private addUserVehicle(vehicle: Auto) {
    const url = `${this.apiUrl}/vehicles/usuario`;

    // Obtener el token del localStorage
    const token = this.getToken();

    if (!token) {
      return throwError(() => new Error('No hay token de autenticación'));
    }

    // Configurar headers con el token de autorización
    const headers = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    return this.http
      .post<ApiMembresiaResponse<Auto>>(url, vehicle, { headers })
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) => {
          console.error('❌ Error agregando vehículo:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Obtiene los vehículos activos del usuario por ID
   */
  private getUserVehicles() {
    this.setIsLoggedIn();
    const idUsuarioDigital = this.getUserData()?.idUsuarioDigital || 0;

    if (!idUsuarioDigital) {
      return throwError(() => new Error('No hay ID de usuario digital'));
    }

    const url = `${this.apiUrl}/vehicles/usuario/${idUsuarioDigital}`;

    // Obtener el token del localStorage
    const token = this.getToken();

    if (!token) {
      return throwError(() => new Error('No hay token de autenticación'));
    }

    // Configurar headers con el token de autorización
    const headers = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    return this.http.get<ApiMembresiaResponse<Auto[]>>(url, { headers }).pipe(
      timeout(10000), // 10 segundos de timeout
      catchError((error) => {
        console.error('❌ Error obteniendo vehículos del usuario:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Elimina un vehículo del usuario desde el backend
   */
  private deleteUserVehicle(vehicleId: number) {
    const url = `${this.apiUrl}/vehicles/${vehicleId}`;

    // Obtener el token del localStorage
    const token = this.getToken();

    if (!token) {
      return throwError(() => new Error('No hay token de autenticación'));
    }

    // Configurar headers con el token de autorización
    const headers = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    return this.http.delete<ApiMembresiaResponse<Auto>>(url, { headers }).pipe(
      timeout(10000), // 10 segundos de timeout
      catchError((error) => {
        console.error('❌ Error eliminando vehículo:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Actualiza el alias de un vehículo
   */
  private updateUserVehicleAlias(vehicleId: number, alias: string) {
    const url = `${this.apiUrl}/vehicles/${vehicleId}/alias`;

    // Obtener el token del localStorage
    const token = this.getToken();

    if (!token) {
      return throwError(() => new Error('No hay token de autenticación'));
    }

    // Configurar headers con el token de autorización
    const headers = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    const payload = { alias };

    return this.http
      .put<ApiMembresiaResponse<Auto>>(url, payload, { headers })
      .pipe(
        timeout(10000), // 10 segundos de timeout
        catchError((error) => {
          console.error('❌ Error actualizando alias:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Actualiza el vehículo predeterminado
   */
  private setUserDefaultVehicle(
    vehicleId: number,
    clearDefault: boolean = false
  ) {
    let queryParams = '';

    if (clearDefault) {
      queryParams = '?clearDefault=true';
    }

    const url = `${this.apiUrl}/vehicles/${vehicleId}/predeterminado${queryParams}`;

    // Obtener el token del localStorage
    const token = this.getToken();

    if (!token) {
      return throwError(() => new Error('No hay token de autenticación'));
    }

    // Configurar headers con el token de autorización
    const headers = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    return this.http.put<ApiMembresiaResponse<Auto>>(url, { headers }).pipe(
      timeout(10000), // 10 segundos de timeout
      catchError((error) => {
        console.error('❌ Error actualizando vehículo predeterminado:', error);
        return throwError(() => error);
      })
    );
  }

  // METODOS DE FLUJO

  private generateId() {
    return Math.floor(Math.random() * 1000000);
  }

  getDefaultVehicle() {
    const vehicles = this.getStoredVehicles();
    return vehicles.find((vehicle) => vehicle.predeterminado);
  }

  async get() {
    this.setIsLoggedIn();

    // Si el usuario está logueado, obtenemos y reemplazamos el localStorage
    if (this.isLoggedIn) {
      const { data: vehicles } = (await firstValueFrom(
        this.getUserVehicles()
      )) || {
        data: [],
      };

      await this.saveVehiclesInLocalStorage(vehicles || []);
      return vehicles || [];
    }

    // Si el usuario no está logueado, obtenemos los vehículos del localStorage

    const vehicles = this.getStoredVehicles();

    await this.saveVehiclesInLocalStorage(vehicles);

    return vehicles;
  }

  async add(vehicle: Auto) {
    this.setIsLoggedIn();

    if (!vehicle.alias?.trim()) {
      vehicle.alias = `${vehicle.anio} ${vehicle.marca} ${vehicle.modelo}`;
    }

    vehicle.anio = Number(vehicle.anio);

    vehicle.fechaCreacion = new Date().toISOString();

    // Si el usuario está logueado, peticionamos al backend
    if (this.isLoggedIn) {
      // Agregamos el vehículo al backend
      await firstValueFrom(this.addUserVehicle(vehicle));

      // Obtenemos los vehículos nuevamente
      const vehicles = await this.get();

      // Obtener el nuevo ID del vehículo
      const nuevoId = this.getLastVehicleId(vehicles);

      // Marcar el vehículo como predeterminado
      await this.setDefaultVehicle(nuevoId);

      // Retornamos los vehículos nuevamente
      return vehicles;
    }

    // Si el usuario no está logueado, obtenemos los vehículos del localStorage y agregamos el vehículo
    const vehicles = await this.get();

    vehicles.push({
      ...vehicle,
      id: this.generateId(),
    });

    // Guardamos los vehículos en el localStorage
    await this.saveVehiclesInLocalStorage(vehicles);

    // Obtener el nuevo ID del vehículo
    const nuevoId = this.getLastVehicleId(vehicles);

    // Marcar el vehículo como predeterminado
    await this.setDefaultVehicle(nuevoId);

    return vehicles;
  }

  getLastVehicleId(vehicles: Auto[]) {
    // Ordenamos por fecha, usando new Date()
    const [firstVehicle] = vehicles.sort(
      (a, b) =>
        new Date(b.fechaCreacion).getTime() -
        new Date(a.fechaCreacion).getTime()
    );

    console.log('firstVehicle', firstVehicle);

    if (!firstVehicle) {
      return 0;
    }

    return firstVehicle.id;
  }

  async remove(vehicleId: number) {
    this.setIsLoggedIn();

    // Si el usuario está logueado, peticionamos al backend
    if (this.isLoggedIn) {
      await firstValueFrom(this.deleteUserVehicle(vehicleId));

      // Obtenemos los vehículos nuevamente
      const vehicles = await this.get();

      // Retornamos los vehículos nuevamente
      return vehicles;
    }

    // Si el usuario no está logueado, obtenemos los vehículos del localStorage y eliminamos el vehículo

    // Filtramos los vehículos para eliminar el vehículo
    const filteredVehicles = ((await this.get()) || []).filter(
      (vehicle) => vehicle.id !== vehicleId
    );

    // Guardamos los vehículos en el localStorage
    await this.saveVehiclesInLocalStorage(filteredVehicles);

    const vehicles = await this.get();

    return vehicles;
  }

  async updateAlias(vehicleId: number, alias: string) {
    this.setIsLoggedIn();

    // Si el usuario está logueado, peticionamos al backend

    if (this.isLoggedIn) {
      await firstValueFrom(this.updateUserVehicleAlias(vehicleId, alias));
      const vehicles = await this.get();
      return vehicles;
    }

    // Si el usuario no está logueado, obtenemos los vehículos del localStorage y actualizamos el alias
    const updatedVehicles = ((await this.get()) || []).map((v) =>
      v.id === vehicleId ? { ...v, alias } : v
    );

    await this.saveVehiclesInLocalStorage(updatedVehicles);

    return updatedVehicles;
  }

  async setDefaultVehicle(vehicleId: number, clearDefault: boolean = false) {
    this.setIsLoggedIn();

    // Si el usuario está logueado, peticionamos al backend
    if (this.isLoggedIn) {
      await firstValueFrom(this.setUserDefaultVehicle(vehicleId, clearDefault));
      const vehicles = await this.get();
      await this.saveVehiclesInLocalStorage(vehicles);
      return vehicles;
    }

    // Si el usuario no está logueado, obtenemos los vehículos del localStorage y actualizamos el vehículo predeterminado
    const vehicles = (await this.get()) || [];
    vehicles.forEach((v) => {
      if (v.id === vehicleId) {
        v.predeterminado = true;
      } else {
        v.predeterminado = false;
      }
    });

    // Si se desea limpiar el vehículo predeterminado, se actualiza el estado de los vehículos
    if (clearDefault) {
      vehicles.forEach((v) => (v.predeterminado = false));
    }

    await this.saveVehiclesInLocalStorage(vehicles);
    return vehicles;
  }

  private async saveVehiclesInLocalStorage(vehicles: Auto[]) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    localStorage.setItem(
      LOCAL_STORAGE_KEY_GARAGE_VEHICLES,
      JSON.stringify(vehicles)
    );

    this.vehiclesSubject.next(vehicles);
  }

  private getStoredVehicles() {
    if (!isPlatformBrowser(this.platformId)) {
      return [];
    }

    const stored = localStorage.getItem(LOCAL_STORAGE_KEY_GARAGE_VEHICLES);
    if (stored) {
      return JSON.parse(stored) as Auto[];
    }
    return [];
  }
}
